import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Center,
  Flex,
  Box,
  Divider,
  Container,
  useTheme,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import BackButton from '../../../components/BackButton';
import {H3, TextG14, TextG16} from '../../../components/typography';
import useAuth from '../../../hooks/useAuth';
import UserAvatar from '../../../components/UserAvatar';
import {SpinnerLoader} from '../../../components/Loader';

// Interface for other user profiles with limited information
// This is what would be returned from an API endpoint for viewing other users
interface OtherUser {
  id: number;
  handle: string;
  profilePhotoUrl: string | null;
  aboutMe: string;
  lifestyleGroups: {id: number; name: string; icon: string}[];
  topics: {id: number; name: string; category_id: number; icon: string | null}[];
  // Required by UserAvatar component but not really used for other users
  name?: string;
  surname?: string;
}

// Realistic mock data based on actual topics and lifestyle groups from the database
const mockOtherUser: OtherUser = {
  id: 999,
  handle: 'neighborKate',
  profilePhotoUrl: 'https://randomuser.me/api/portraits/women/42.jpg',
  aboutMe:
    'Food enthusiast, dog lover, and local community advocate. Always looking for new cafes and interesting spots around the neighborhood!',
  lifestyleGroups: [
    {id: 1, name: 'Live Healthy', icon: 'health'},
    {id: 5, name: 'Meet People', icon: 'moderator'},
    {id: 6, name: 'Experience More', icon: 'rocket'},
    {id: 4, name: 'Support Local Causes', icon: 'hand'},
  ],
  topics: [
    {id: 72, name: 'Dogs', category_id: 1, icon: null},
    {id: 152, name: 'Coffee', category_id: 2, icon: null},
    {id: 107, name: 'Local Charities', category_id: 1, icon: null},
    {id: 131, name: 'Craft Cocktails', category_id: 2, icon: null},
    {id: 197, name: 'Meeting People', category_id: 5, icon: 'fi-rr-handshake'},
    {id: 69, name: 'Breakfast', category_id: 2, icon: null},
    {id: 118, name: 'Classes', category_id: 1, icon: null},
    {id: 211, name: 'Chinese Food', category_id: 2, icon: null},
    {id: 67, name: 'Brunch', category_id: 2, icon: null},
  ],
};

// Map category IDs to category names
const categoryMap = {
  1: 'Activities',
  2: 'Dining',
  3: 'Nightlife',
  4: 'Wellbeing',
  5: 'Social',
  6: 'Shopping',
  7: 'Apartment',
};

function OtherUserProfile() {
  const navigate = useNavigate();
  const theme = useTheme();
  const {user} = useAuth();
  const {handle} = useParams();
  const [otherUser, setOtherUser] = useState<OtherUser | null>(null);

  // In the future, fetch user data from API
  useEffect(() => {
    // Mock API call
    // TODO: Replace with actual API call to get another user's profile
    setTimeout(() => {
      setOtherUser(mockOtherUser);
    }, 300);
  }, [handle]);

  // Find common lifestyle groups and topics
  const commonLifestyleGroups =
    otherUser?.lifestyleGroups.filter((group) =>
      user?.lifestyleGroups.some((userGroup) => {
        console.log('userGroup', userGroup, group);
        return Number(userGroup.id) === group.id;
      })
    ) || [];

  const commonTopics =
    otherUser?.topics.filter((topic) =>
      user?.topics.some((userTopic) => Number(userTopic.id) === topic.id)
    ) || [];

  // Group topics by category
  const groupedTopics = otherUser
    ? Object.entries(
        otherUser.topics.reduce((acc, topic) => {
          const categoryId = topic.category_id;
          const categoryName = categoryMap[categoryId] || 'Other';

          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }

          acc[categoryName].push(topic);
          return acc;
        }, {} as Record<string, typeof otherUser.topics>)
      )
    : [];

  if (!otherUser) {
    return (
      <Container h="auto !important" pb={10}>
        <Box pt={6}>
          <Flex alignItems="center">
            <BackButton pos={'relative'} onClick={() => navigate(-1)} sticky={false} />
            <Box flex="1">
              <Center ml="-35px">
                <H3>Profile</H3>
              </Center>
            </Box>
          </Flex>
        </Box>
        <Center h="60vh">
          <SpinnerLoader />
        </Center>
      </Container>
    );
  }

  // Function to determine if a topic or lifestyle group is common
  const isCommonTopic = (topicId: number) => commonTopics.some((t) => t.id === topicId);
  const isCommonLifestyle = (groupId: number) =>
    commonLifestyleGroups.some((g) => g.id === groupId);

  return (
    <Container h="auto">
      <Box pt={6}>
        <Flex alignItems="center">
          <BackButton pos={'relative'} onClick={() => navigate(-1)} sticky={false} />
          <Box flex="1">
            <Center ml="-35px">
              <H3>Profile</H3>
            </Center>
          </Box>
        </Flex>
      </Box>

      {/* Profile Header */}
      <Box mt="24px" py={5}>
        <Flex px="4px" alignItems="center" flexDirection="column">
          <UserAvatar size="xl" user={otherUser as any} />
          <Box mt={3}>
            <TextG16 fontWeight="bold" textAlign="center">
              @{otherUser.handle}
            </TextG16>
          </Box>
        </Flex>
      </Box>

      {/* About Me Section */}
      <Box px={4} py={4} bg={theme.colors.veryLightBlue} borderRadius="md" mt={2} mb={6}>
        <TextG14 fontWeight="500">About Me</TextG14>
        <TextG14 mt={2}>{otherUser.aboutMe}</TextG14>
      </Box>

      <Divider />

      {/* Common Interests Section */}
      {(commonLifestyleGroups.length > 0 || commonTopics.length > 0) && (
        <Box mt={6} mb={6}>
          <TextG14 fontWeight="bold" color={theme.colors.darkGrey} mb={3}>
            You are both interested in
          </TextG14>

          {commonLifestyleGroups.length > 0 && (
            <Box mb={2}>
              {/* <TextG12 color={theme.colors.grey} mb={2}>
                Lifestyle Goals You Share
              </TextG12> */}
              <Flex flexWrap="wrap" gap={2}>
                {commonLifestyleGroups.map((group) => (
                  <Tag
                    key={group.id}
                    size="md"
                    borderRadius="full"
                    variant="solid"
                    colorScheme="blue"
                    bg={theme.colors.lightestBlue}
                    color={theme.colors.blue}>
                    <TagLabel>{group.name}</TagLabel>
                  </Tag>
                ))}
              </Flex>
            </Box>
          )}

          {commonTopics.length > 0 && (
            <Box>
              {/* <TextG12 color={theme.colors.grey} mb={2}>
                Topics You Share
              </TextG12> */}
              <Flex flexWrap="wrap" gap={2}>
                {commonTopics.map((topic) => (
                  <Tag
                    key={topic.id}
                    size="md"
                    borderRadius="full"
                    variant="solid"
                    colorScheme="blue"
                    bg={theme.colors.lightestBlue}
                    color={theme.colors.blue}>
                    <TagLabel>{topic.name}</TagLabel>
                  </Tag>
                ))}
              </Flex>
            </Box>
          )}
        </Box>
      )}

      <Divider />

      {/* All Lifestyle Groups */}
      <Box mt={6} mb={6}>
        <TextG16 fontWeight="bold" mb={3}>
          <span style={{color: theme.colors.rose}}>@{otherUser.handle}</span> other interests
        </TextG16>
        <TextG14 fontWeight="500" color={theme.colors.darkGrey} mb={3}>
          Lifestyle Goals
        </TextG14>
        <Flex flexWrap="wrap" gap={2}>
          {otherUser.lifestyleGroups.map((group) => (
            <Tag
              key={group.id}
              size="md"
              borderRadius="full"
              variant={isCommonLifestyle(group.id) ? 'solid' : 'outline'}
              colorScheme={isCommonLifestyle(group.id) ? 'blue' : 'gray'}
              outline={
                isCommonLifestyle(group.id)
                  ? '0px solid transparent'
                  : `1px solid ${theme.colors.grey}`
              }
              bg={isCommonLifestyle(group.id) ? theme.colors.lightestBlue : 'transparent'}
              color={isCommonLifestyle(group.id) ? theme.colors.blue : theme.colors.grey}
              borderColor={isCommonLifestyle(group.id) ? theme.colors.blue : theme.colors.grey}>
              <TagLabel>{group.name}</TagLabel>
            </Tag>
          ))}
        </Flex>
      </Box>

      {/* Topics by Category */}
      <Box mt={6} mb={10}>
        <TextG14 fontWeight="500" color={theme.colors.darkGrey} mb={3}>
          Interests by Category
        </TextG14>

        {groupedTopics.map(([category, topics]) => (
          <Box key={category} mb={4}>
            <TextG14 fontWeight="500" color={theme.colors.darkGrey} mb={2}>
              {category}
            </TextG14>
            <Flex flexWrap="wrap" gap={2}>
              {topics.map((topic) => (
                <Tag
                  key={topic.id}
                  size="md"
                  borderRadius="full"
                  variant={isCommonTopic(topic.id) ? 'solid' : 'outline'}
                  outline={
                    isCommonTopic(topic.id)
                      ? '0px solid transparent'
                      : `1px solid ${theme.colors.grey}`
                  }
                  colorScheme={isCommonTopic(topic.id) ? 'teal' : 'gray'}
                  bg={isCommonTopic(topic.id) ? theme.colors.lightestBlue : 'transparent'}
                  color={isCommonTopic(topic.id) ? theme.colors.blue : theme.colors.grey}
                  borderColor={isCommonTopic(topic.id) ? theme.colors.blue : theme.colors.grey}>
                  <TagLabel>{topic.name}</TagLabel>
                </Tag>
              ))}
            </Flex>
          </Box>
        ))}
      </Box>
    </Container>
  );
}

export default OtherUserProfile;
